// Code to run Facebookpixel scripts in specific areas of user flow
import Analytics from "../Analytics";
import get from "lodash/get";
import find from "lodash/find";
import Sha256 from "crypto-js/sha256";
import {
  FACEBOOK_EVENTS_MAPPING,
  FACEBOOK_ACTION_TYPE,
  GTM_DATALAYER_FACEBOOK_EVENT_MAPPING,
  FACEBOOK_VALID_COUNTRIES,
  FB_EVENTS_NAME
} from "../../constants/AnalyticsTypeConstants";
import { COUNTRY, forms, PHONE_ONLY_CHANNEL } from "utils/constants";
import { currencyCode } from "analytics";
import { v4 as uuid } from "uuid";
import { getChannelFromUrl } from "utils/helpers";

const FACEBOOKPIXEL_INTERVAL = 1000;
const FACEBOOKPIXEL_INTERVAL_TIMEOUT = 10000;

const runFbq = (action, eventName, customParams) => {
  /**
   * Helper function to run fbq when fbq is not defined in window yet
   * @param {string} action Facebookpixel-related action to execute
   * @param {string} eventName event name to be shown in Events Manager in Facebook Business Manager
   */
  let intervalID = setInterval(() => {
    if (window.fbq) {
      window.fbq(action, eventName, customParams);
      clearInterval(intervalID);
    } else {
      console.log("Facebook pixel undefined, delaying...");
    }
  }, FACEBOOKPIXEL_INTERVAL);

  // stop runFbq from continuously executing if country does not use facebookpixel
  setTimeout(() => clearInterval(intervalID), FACEBOOKPIXEL_INTERVAL_TIMEOUT);
};

class FacebookpixelAnalytics extends Analytics {
  pushEvent(event, data, country = COUNTRY.toLowerCase()) {
    /**
     * Push facebookpixel events to Events Manager in Facebook Business Manager
     * @param {string} event type of event triggered based on user actions, page load and etc.
     * @param {object} data data associated with triggered event
     */
    const path = get(window, "location.pathname", ""); // for targetting specific areas of user flow
    data = {
      event_id: uuid(),
      ...data
    };
    this.pushFacebookPixelEvent(event, data, path, country, runFbq);
    this.pushGtmDataLayer(event, data, path, country);
  }

  pushFacebookPixelEvent(event, data, currentUrl, country, runFbq) {
    // check if facebookpixel uses event
    if (FACEBOOK_EVENTS_MAPPING[event]) {
      // check if current country uses facebookpixel
      const countrySupported = find(FACEBOOK_EVENTS_MAPPING[event], country);

      if (countrySupported) {
        // check if current page url uses facebookpixel
        const urlUsesFbPixel = find(
          FACEBOOK_EVENTS_MAPPING[event].valid_countries[country],
          ["trigger_urls", [currentUrl]]
        );

        // push facebookpixel event if page uses facebookpixel
        if (urlUsesFbPixel) {
          urlUsesFbPixel.events.forEach(fbPixelEvent => {
            let customParams = constructCustomParams(data, fbPixelEvent);

            // push facebookpixel event for specific conditions (for example, only after form submission)
            if (urlUsesFbPixel.condition) {
              urlUsesFbPixel.condition(
                data,
                runFbq,
                FACEBOOK_ACTION_TYPE.track,
                fbPixelEvent,
                customParams
              );
              return;
            }

            // for all other events not submitted conditionally
            runFbq(FACEBOOK_ACTION_TYPE.track, fbPixelEvent, customParams);
          });
        }
      }
    }
  }

  pushGtmDataLayer(
    event,
    data,
    currentUrl,
    country,
    userDetailsHashed = {},
    dataLayer = window.dataLayer || []
  ) {
    if (GTM_DATALAYER_FACEBOOK_EVENT_MAPPING[event]) {
      // check if current country uses gtm datalayer
      const countrySupported = find(
        GTM_DATALAYER_FACEBOOK_EVENT_MAPPING[event],
        country
      );
      if (countrySupported) {
        // check if current page url uses gtm datalayer
        const urlUsesGTMDataLayer = find(
          GTM_DATALAYER_FACEBOOK_EVENT_MAPPING[event].valid_countries[country],
          ["trigger_urls", [currentUrl]]
        );
        // push user details if page uses gtm datalayer
        if (urlUsesGTMDataLayer) {
          // construct user data based on form and event
          const { userInfo, hasForm } = constructUserDetails(
            data,
            urlUsesGTMDataLayer.formType,
            urlUsesGTMDataLayer.dataLayerEventType,
            country
          );
          // prevent pushing to datalayer if form does not exist
          // (if user refreshes payment page and personal details form disappear)
          if (!hasForm) return;

          // hash sensitive user data and push to dataLayer
          userDetailsHashed = hashUserDetails(userInfo, country);
          dataLayer.push(userDetailsHashed);
        }
      }
    }
  }
}

const constructCustomParams = (data, fbPixelEvent) => {
  /**
   * Construct parameters to be passed into facebook pixel event
   */
  let customParams = {};
  switch (fbPixelEvent) {
    case FB_EVENTS_NAME.PURCHASE:
      customParams = {
        event_id: get(data, "event_id", null),
        value: get(data, "orderParams.order_summary.onetime_total_fee", 0),
        currency: currencyCode(),
        order_id: get(data, "order_ref", null)
      };
      break;
    default:
      break;
  }
  return customParams;
};

const hash256 = data => {
  return Sha256(data).toString();
};

const hashUserDetails = ({
  event,
  event_id,
  fb_transaction_id,
  fb_email_addr,
  fb_tel_no,
  fb_full_name,
  fb_first_name,
  fb_last_name,
  fb_city,
  fb_zip,
  country
}) => {
  // Hashes all sensitive data
  let name = {};
  // Include full_name property only if SG
  // Otherwise, first_name and last_name property
  if (country === FACEBOOK_VALID_COUNTRIES.SG) {
    name = { fb_full_name: hash256(fb_full_name) };
  } else {
    name = {
      fb_first_name: hash256(fb_first_name),
      fb_last_name: hash256(fb_last_name)
    };
  }
  return {
    event: event,
    event_id: event_id,
    fb_transaction_id: fb_transaction_id,
    fb_email_addr: hash256(fb_email_addr),
    fb_tel_no: hash256(fb_tel_no),
    fb_city: hash256(fb_city),
    fb_zip: hash256(fb_zip),
    ...name
  };
};

const constructUserDetails = (
  data,
  formType,
  gtmDataLayerEventType,
  country
) => {
  /**
   * Construct user details based on the type of url and country
   * (since each url has their own forms, and each country has different ways of displaying name
   * (i.e., Singapore === full_name, Other countries === first_name, last_name)
   * @param {object} data Whole object that contains a property with SelfActivationForm, DeliveryInfoForm, PersonalDetailsForm, etc.
   * @param {string} formType Form type to use
   * @return {[{}, boolean]} An array of two elements. First element is the userDetails to be pushed into fbDataLayer, and the second element is a boolean of whether form exists
   */
  const form = get(data, "forms", null); // data: {forms: {SelfActivationForm, ...}}

  // exit function if form doesn't exist
  // (when user refreshes payment page, form removed, etc.)
  // to prevent submission of empty data
  if (!get(form, formType, null)) return { userInfo: {}, hasForm: false };

  // SG has different property names from other countries
  let name = {};
  if (country === FACEBOOK_VALID_COUNTRIES.SG) {
    name = {
      fb_full_name: get(
        form,
        `${formType}.values.full_name`,
        get(form, `${formType}.values.first_name`, null)
      )
    };
  } else {
    name = {
      fb_first_name: get(form, `${formType}.values.first_name`, null),
      fb_last_name: get(form, `${formType}.values.last_name`, null)
    };
  }

  // initialize common properties across all formTypes
  let userDetails = {
    event: gtmDataLayerEventType,
    event_id: get(data, "event_id", null),
    fb_transaction_id: get(data, "order_ref", null),
    fb_tel_no: get(form, `${formType}.values.contact_number`, null),
    ...name
  };

  // additional user details defined in switch case because each formType has
  // different property names that refer to the same type of data required
  switch (formType) {
    case forms.SELF_ACTIVATION_FORM:
      userDetails = buildUserDetailsBySelfActivationForm(
        userDetails,
        form,
        country
      );
      break;
    case forms.DELIVERY_INFO_FORM:
      userDetails = buildUserDetailsByDeliveryInfoForm(
        userDetails,
        form,
        country
      );
      break;
    case forms.PERSONAL_DETAILS_FORM:
      userDetails = buildUserDetailsByPersonalDetailsForm(
        userDetails,
        form,
        country
      );
      break;
    default:
      break;
  }

  return { userInfo: { ...userDetails }, hasForm: true };
};

const buildUserDetailsBySelfActivationForm = (userDetails, form, country) => {
  const selfActFormValues = get(form, "SelfActivationForm.values", null);
  userDetails = {
    ...userDetails,
    fb_email_addr: get(selfActFormValues, "email", null)
  };
  if (get(selfActFormValues, "delivery_method.action", null) === "home") {
    // Home delivery option
    userDetails = {
      ...userDetails,
      fb_city: get(selfActFormValues, "delivery_city", null),
      fb_zip: get(selfActFormValues, "delivery_zip_code", null)
    };
  } else if (
    get(selfActFormValues, "delivery_method.action") === "pop_station"
  ) {
    // Pop station delivery option
    userDetails = {
      ...userDetails,
      fb_city: get(
        selfActFormValues,
        "pop_station_outlet_full_data.country",
        null
      ),
      fb_zip: get(
        selfActFormValues,
        "pop_station_outlet_full_data.postal_code",
        null
      )
    };
  }

  return userDetails;
};

const buildUserDetailsByDeliveryInfoForm = (userDetails, form, country) => {
  const selfDelInfoFormValues = get(form, "DeliveryInfoForm.values", null);
  const personalDetsFormValues = get(form, "PersonalDetailsForm.values", null);

  let name = {};
  let addressDetails = {};
  // SG & other countries have different property names for the same type of information
  if (country === FACEBOOK_VALID_COUNTRIES.SG) {
    if (getChannelFromUrl() === PHONE_ONLY_CHANNEL) {
      name = {
        // first name is actually storing the full name in sg phoneonly.
        // so we are using first name here instead of full_name
        fb_full_name: get(
          personalDetsFormValues,
          "first_name",
          get(personalDetsFormValues, "full_name", null)
        )
      };
    } else {
      name = {
        fb_full_name: get(
          personalDetsFormValues,
          "full_name",
          get(personalDetsFormValues, "first_name", null)
        )
      };
    }
    addressDetails = {
      fb_city: get(selfDelInfoFormValues, "billing_city", "Singapore", null),
      fb_zip: get(selfDelInfoFormValues, "billing_zip_code", null)
    };
  } else {
    name = {
      fb_first_name: get(personalDetsFormValues, "first_name", null),
      fb_last_name: get(personalDetsFormValues, "last_name", null)
    };
    addressDetails = {
      fb_city: get(selfDelInfoFormValues, "delivery_city", null),
      fb_zip: get(selfDelInfoFormValues, "delivery_zip_code", null)
    };
  }
  userDetails = {
    ...userDetails,
    fb_tel_no: get(personalDetsFormValues, "contact_number", null),
    fb_email_addr: get(personalDetsFormValues, "email", null),
    ...name,
    ...addressDetails
  };

  return userDetails;
};

const buildUserDetailsByPersonalDetailsForm = (userDetails, form, country) => {
  const selfDelInfoFormValues = get(form, "DeliveryInfoForm.values");
  const personalDetsFormValues = get(form, "PersonalDetailsForm.values");
  let name = {};
  let addressDetails = {};
  // SG & other countries have different property names for the same type of information
  if (country === FACEBOOK_VALID_COUNTRIES.SG) {
    name = {
      fb_full_name: get(
        personalDetsFormValues,
        "full_name",
        get(personalDetsFormValues, "first_name", null)
      )
    };
    addressDetails = {
      fb_city: get(personalDetsFormValues, "billing_city", "Singapore", null),
      fb_zip: get(personalDetsFormValues, "billing_zip_code", null)
    };
  } else {
    name = {
      fb_first_name: get(personalDetsFormValues, "first_name", null),
      fb_last_name: get(personalDetsFormValues, "last_name", null)
    };
    addressDetails = {
      fb_city: get(selfDelInfoFormValues, "delivery_city", null),
      fb_zip: get(selfDelInfoFormValues, "delivery_zip_code", null)
    };
  }

  userDetails = {
    ...userDetails,
    fb_email_addr: get(personalDetsFormValues, "email", null),
    fb_tel_no: get(personalDetsFormValues, "contact_number", null),
    ...name,
    ...addressDetails
  };

  return userDetails;
};

export default new FacebookpixelAnalytics();
